import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { AutocompleteSearch, Button } from 'components/UIElements'
import { pluralize } from 'utils/misc'
import '../../stylesheets/EditSites.scss'
import Tags from '../../../../../Participants/routes/CreateParticipant/components/Tags'

const EditSites = ({
  closeModal,
  hasError,
  checkedSites = [],
  disabled = false,
  instrument,
  isAnnouncement = false,
  errorText: propErrorText,
  leafSites,
  setPromotionSites,
  siteSelectionList = [],
  saveInstrumentSitesForPromotion,
  studyID,
}) => {
  const [checked, setChecked] = useState({})
  const [errorText, setErrorText] = useState(propErrorText)
  const [initialCheckedSites] = useState(() => checkedSites)

  // Memoize the processed list to avoid recalculating on every render
  const list = useMemo(() => {
    return siteSelectionList.map(row => ({
      key: row[0].value,
      text: row[1].value,
      treeLevel: row[2].value,
      path: row[3].value,
    }))
  }, [siteSelectionList])

  // Derive tags from the current checked state
  const tags = useMemo(() => {
    return Object.entries(checked).map(([key, text]) => ({
      key,
      text,
    }))
  }, [checked])

  // Initialize checked sites
  useEffect(() => {
    const initialChecked = list
      .filter(site => checkedSites.includes(site.key))
      .reduce((acc, site) => {
        acc[site.key] = site.text
        return acc
      }, {})

    setChecked(initialChecked)
    setPromotionSites(initialChecked)
  }, [list, checkedSites, setPromotionSites])

  const onToggleSite = useCallback(
    siteId => {
      setChecked(prevChecked => {
        const newChecked = { ...prevChecked }
        if (siteId in newChecked) {
          delete newChecked[siteId]
        } else {
          const site = list.find(item => item.key === siteId)
          if (site) {
            newChecked[siteId] = site.text
          }
        }
        setPromotionSites(newChecked)
        return newChecked
      })
      setErrorText('') // Clear error when user makes a change
    },
    [list, setPromotionSites],
  )

  const handleConfirm = useCallback(() => {
    const currentSiteIds = Object.keys(checked)
    const initialSiteIds = initialCheckedSites

    // Check if no sites are selected
    if (currentSiteIds.length === 0) {
      setErrorText('You must assign at least one site')
      return
    }

    // Check if sites haven't changed
    if (currentSiteIds.length === initialSiteIds.length && currentSiteIds.every(id => initialSiteIds.includes(id))) {
      closeModal()
      return
    }

    try {
      saveInstrumentSitesForPromotion(studyID, instrument, checked, isAnnouncement)
      closeModal()
    } catch (err) {
      if (__DEV__) console.error(err)
      setErrorText('Failed to save sites')
    }
  }, [checked, initialCheckedSites, studyID, instrument, closeModal, saveInstrumentSitesForPromotion])

  return (
    <div className='sites-edition'>
      <h2>Edit Sites</h2>
      {tags.length > 0 ? (
        <>
          <p>
            <b>{tags.length}</b> {pluralize(tags.length, 'site', 'sites', false)} assigned
          </p>
          <Tags disabled={disabled} toggle={onToggleSite} tags={tags} />
        </>
      ) : null}
      <AutocompleteSearch
        checked={checked}
        errorText={errorText}
        filter={leafSites}
        hasError={hasError || !!errorText}
        list={list}
        id='search-for-site-input'
        placeholder='Search for a site...'
        toggleItem={onToggleSite}
        disabled={disabled}
      />
      <div className='edit-sites-buttons'>
        <Button grey id='modal-cancel' content='Cancel' onClick={closeModal} />
        <Button id='modal-confirm' disabled={disabled} className='confirm' content='Confirm' onClick={handleConfirm} />
      </div>
    </div>
  )
}

export default React.memo(EditSites)
